// Loan record foundations — the "Loan Administration fields" section of the
// Profile tab: admin-defined fields with per-loan values, editable in place
// by Loans.Manage holders (saved on blur / change). Field definitions are
// managed by ADMIN via <ProfileFieldsManageModal>.

import { useState } from "react";
import { Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useLoanProfileFieldValues,
  useSetLoanProfileFieldValue,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { ProfileFieldsManageModal } from "./ProfileFieldsManageModal";
import { calendarDate } from "../common/colorBand";
import type { LoanProfileFieldValueDto } from "@/api/portfolioAdmin/types";

interface LoanAdminFieldsCardProps {
  loanId: string;
}

export function LoanAdminFieldsCard({ loanId }: LoanAdminFieldsCardProps) {
  const auth = useAuth();
  const isAdmin = auth.hasRoleCode("ADMIN");
  const canEdit = auth.hasPermission("Loans.Manage") || isAdmin;
  const fields = useLoanProfileFieldValues(loanId);
  const [manageOpen, setManageOpen] = useState(false);

  const rows = fields.data ?? [];

  return (
    <Card data-testid="loan-admin-fields">
      <div className="flex items-center justify-between mb-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            Loan Administration fields
          </div>
          <p className="text-caption text-gray-mid m-0">Added post-close</p>
        </div>
        {isAdmin && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setManageOpen(true)}
            data-testid="manage-profile-fields"
          >
            Manage fields
          </Button>
        )}
      </div>

      {fields.isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading fields…</p>
      ) : fields.isError ? (
        <p className="text-small text-status-error m-0">
          Could not load administration fields. Refresh to retry.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0" data-testid="admin-fields-empty">
          No administration fields defined yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
          {rows.map((f) => (
            <FieldRow key={f.definitionId} loanId={loanId} field={f} canEdit={canEdit} />
          ))}
        </div>
      )}

      {isAdmin && (
        <ProfileFieldsManageModal
          loanId={loanId}
          open={manageOpen}
          onClose={() => setManageOpen(false)}
        />
      )}
    </Card>
  );
}

function displayValue(field: LoanProfileFieldValueDto): string {
  if (field.value == null || field.value === "") return "—";
  if (field.fieldType === "yesno") return field.value === "true" ? "Yes" : "No";
  if (field.fieldType === "date") return calendarDate(field.value);
  return field.value;
}

function FieldRow({
  loanId,
  field,
  canEdit,
}: {
  loanId: string;
  field: LoanProfileFieldValueDto;
  canEdit: boolean;
}) {
  const toast = useToast();
  const setValue = useSetLoanProfileFieldValue(loanId);
  const [draft, setDraft] = useState(field.value ?? "");

  const save = async (value: string) => {
    if ((value || null) === (field.value || null)) return; // unchanged
    try {
      await setValue.mutateAsync({
        definitionId: field.definitionId,
        value: value === "" ? null : value,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setDraft(field.value ?? "");
    }
  };

  return (
    <div data-testid={`admin-field-${field.key}`}>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {field.label}
      </div>
      {!canEdit ? (
        <div className="text-onyx mt-[2px]">{displayValue(field)}</div>
      ) : field.fieldType === "yesno" ? (
        <select
          className="mt-1 px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            void save(e.target.value);
          }}
          aria-label={field.label}
        >
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      ) : (
        <input
          type={field.fieldType === "date" ? "date" : "text"}
          inputMode={field.fieldType === "number" ? "decimal" : undefined}
          className="mt-1 w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void save(draft.trim())}
          aria-label={field.label}
        />
      )}
    </div>
  );
}
